import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "../../widgets/modal";
import { BENCHMARK_CONTACT, BENCHMARK_SINGLEMAP } from "../../constants/app.constants";
import {
  updateSingleMapScheduled,
  fetchMapScheduled,
  deleteMapImageScheduled,
} from "./scheduling.utils";
import { fetchBenchmarkContact } from "../overview/overview.utils";
import { useQuery } from "@tanstack/react-query";
import { FC } from "react";
import Swal from "sweetalert2";
import Loader from "../../widgets/Loader";
import TextArea from "../../widgets/textArea";
import { SCHEDULING_DIALOGS } from "../../constants/dialog.constants";

type OverviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  mapId: number;
};

type UpdatedValue = string | Date;

const SchedulingRequestChangeModal: FC<OverviewModalProps> = (props) => {
  const { mapId, isOpen, onClose, onSubmit } = props;
  const accountManagerId = window.localStorage.getItem("AccountManagerID");

  const [propertyName, setPropertyName] = useState("");
  const [addressBlock, setAddressBlock] = useState("");
  const [companyOwnerContact, setCompanyOwnerContact] = useState("");
  const [companyOwnerContactMobilePhone, setCompanyOwnerContactMobilePhone] =
    useState("");
  const [companyOwnerContactPhone, setCompanyOwnerContactPhone] = useState("");
  const [companyOwnerContactEmail, setCompanyOwnerContactEmail] = useState("");
  const [companyMap, setCompanyMap] = useState("");
  const [companyRequestChange, setCompanyRequestChange] = useState("");

  const [isShowLoader, setIsShowLoader] = useState(false);
  const [validationMsg, setValidationMsg] = useState("");

  // const { isFetching, data: dataBenchmarkSingleMap } = useQuery({
  //   queryKey: [BENCHMARK_SINGLEMAP, mapId],
  //   queryFn: () => fetchMapScheduled(mapId),
  //   enabled: !!mapId,
  //   refetchOnWindowFocus: false,
  // });

  const shouldFetch = isOpen && !!mapId;

  const { isFetching, data: dataBenchmarkSingleMap } = useQuery({
  queryKey: [BENCHMARK_SINGLEMAP, mapId],
  queryFn: () => fetchMapScheduled(mapId),
  enabled: shouldFetch,
  refetchOnWindowFocus: false,
  });

  const {
    isFetching: isFetchingBenchmarkContact,
    data: dataBenchmarkContact,
  } = useQuery({
    queryKey: [BENCHMARK_CONTACT, accountManagerId],
    queryFn: () => fetchBenchmarkContact(accountManagerId),
    enabled: isOpen && !!accountManagerId,
    refetchOnWindowFocus: false,
  });

  const onChangeFieldValues = useCallback(
    (id: string, updatedValue: UpdatedValue) => {
      if (id == "companyRequestChange") {
        setCompanyRequestChange(updatedValue as string);
      }
    },
    []
  );

  useEffect(() => {
    if (mapId && !isFetching) {
      setPropertyName(dataBenchmarkSingleMap?.PropertyName);
      setAddressBlock(dataBenchmarkSingleMap?.AddressBlock);
      setCompanyMap(dataBenchmarkSingleMap?.CompanyMap);
      setCompanyRequestChange(dataBenchmarkSingleMap?.CompanyRequestChangeMap);

      setIsShowLoader(false);
    }
  }, [dataBenchmarkSingleMap, mapId]);

  useEffect(() => {
    if (dataBenchmarkContact) {
      setCompanyOwnerContact(dataBenchmarkContact?.NameFull || "");
      setCompanyOwnerContactMobilePhone(
        dataBenchmarkContact?.Phone_Mobile || ""
      );
      setCompanyOwnerContactPhone(
        dataBenchmarkContact?.Phone_Office || ""
      );
      setCompanyOwnerContactEmail(
        dataBenchmarkContact?.Email_Office || ""
      );
    }
  }, [dataBenchmarkContact]);

  const handleSubmit = async () => {
    if (companyRequestChange === "") {
      setValidationMsg("Please Enter Change Request");
    } else {
      if (mapId) {
        setIsShowLoader(true);
        updateSingleMapScheduled(mapId, companyRequestChange).then(
          (updateResult) => {
            setIsShowLoader(false);
            if (updateResult) {
              Swal.fire({
                customClass: {
                  popup: "sweetAlert",
                },
                icon: "success",
                showConfirmButton: false,
                timer: 1500,
                title: SCHEDULING_DIALOGS.requestSentSuccess,
              });
              // onClose();
              onSubmit();
            } else {
              Swal.fire({
                customClass: ".swal2-confirm",
                icon: "warning",
                title: SCHEDULING_DIALOGS.requestSendFailed,
              });
            }
          }
        );
      }
    }
  };

  const handleCancel = () => {
    if (companyMap) {
      void deleteMapImageScheduled(companyMap).catch((error) => {
        console.error("Failed to delete map image", error);
      });
    }
    setCompanyRequestChange("");
    setIsShowLoader(false);
    setValidationMsg("");
    onClose();
  };
  return (
    <Modal show={isOpen} label="Map" onClose={handleCancel}>
      {validationMsg !== "" ? (
        <div className="text-danger">{validationMsg}</div>
      ) : null}
      <div className="requestModalSection">
        <div className="modalForm">
          {isShowLoader || isFetching || isFetchingBenchmarkContact ? <Loader /> : null}
          <form>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group row mb-3">
                    <div className="col-md-8 fw-bold">{propertyName}</div>
                  </div>
                  <div className="form-group row mb-3">
                    <div className="col-md-8">{addressBlock}</div>
                  </div>
                  <div className="form-group row mb-3">
                    <label className="col-md-4 col-form-label fw-bold">
                      Ownership:
                    </label>
                    <div className="col-md-8 fw-bold mt-2">
                      {companyOwnerContact}
                    </div>
                  </div>
                  <div className="form-group row mb-3">
                    <label className="col-md-4 col-form-label">
                      Cell Phone:
                    </label>
                    <div className="col-md-8 mt-2">
                      {companyOwnerContactMobilePhone}
                    </div>
                  </div>

                  <div className="form-group row mb-3">
                    <label className="col-md-4 col-form-label">
                      Office Phone:
                    </label>
                    <div className="col-md-8 mt-2">
                      {companyOwnerContactPhone}
                    </div>
                  </div>
                  <div className="form-group row mb-3">
                    <label className="col-md-4 col-form-label">Email:</label>
                    <div className="col-md-8 mt-2">
                      {companyOwnerContactEmail}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group row mb-3">
                    <div className="col-md-8 fw-bold">
                      {companyMap && (
                        <img
                          src={companyMap}
                          alt="Company Map"
                          className="custom-img"
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div className="row order-md-2">
                  <div className="form-group row mb-3">
                    <label className="col-md-4 col-form-label fw-bold lblMap">
                      Request to Change Map:
                      <span className="text-danger">*</span>
                    </label>
                    <div className="col-md-8 mt-2 w-80 textareaMap">
                      <TextArea
                        id="companyRequestChange"
                        className="form-control textareaInput"
                        onChange={onChangeFieldValues}
                        value={companyRequestChange}
                        rows={4}
                      />
                    </div>
                  </div>
                </div>
                <div className="row order-md-4">
                  <div className="form-group row">
                    <div className="col-md-2"></div>
                    <div className="col-md-8 ms-md-2 actionButtons">
                      <button
                        type="button"
                        className="btn cancelPwdBtn mb-3"
                        onClick={handleCancel}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn submitBtn mb-3"
                        onClick={handleSubmit}
                      >
                        Request Change
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default SchedulingRequestChangeModal;
